import { ref } from 'vue';
import type { Unit, UnitMember } from '@/types/Groups';
import { useApi } from '@/composables/useApi.ts';

const units = ref<Unit[]>([]);

export function useGroupService(companyId: string) {
  const { deepAdminBackendAPI } = useApi();
  const unitsLoading = ref(false);
  const unitsError = ref<string | null>(null);

  const members = ref<UnitMember[]>([]);
  const membersLoading = ref(false);
  const membersError = ref<string | null>(null);

  async function fetchUnits(): Promise<void> {
    if (!companyId) return;
    unitsLoading.value = true;
    unitsError.value = null;
    try {
      const { data } =
        await deepAdminBackendAPI.value.accountsUnitsV2.get(companyId);
      units.value = data.items;
    } catch (e) {
      unitsError.value =
        e instanceof Error ? e.message : 'Failed to fetch groups';
      units.value = [];
    } finally {
      unitsLoading.value = false;
    }
  }

  async function fetchUnitMembers(unitId: string): Promise<void> {
    if (!unitId) return;
    membersLoading.value = true;
    membersError.value = null;
    try {
      const { data } = await deepAdminBackendAPI.value.accountsMembersV2.get(
        unitId,
        {
          subjectType: 'user,service_user',
          state: 'active,pending',
          page: 1,
          size: 25,
          order: 'subject.primaryEmail'
        }
      );
      members.value = data.items;
    } catch (e) {
      membersError.value =
        e instanceof Error ? e.message : 'Failed to fetch members';
      members.value = [];
    } finally {
      membersLoading.value = false;
    }
  }

  function resetMembers(): void {
    members.value = [];
    membersError.value = null;
  }

  return {
    units,
    unitsLoading,
    unitsError,
    fetchUnits,
    members,
    membersLoading,
    membersError,
    fetchUnitMembers,
    resetMembers
  };
}
